import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
    ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
    Legend, ResponsiveContainer,
} from "recharts";
import { useAuth } from "../context/AuthContext";
import { useNotification } from "../context/NotificationContext";
import { useConfirmation } from "../context/ConfirmationContext";
import API_URL from "../config";

const MESES = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
const formatarPeriodo = (p) => {
    if (!p || p.length < 6) return p;
    return `${MESES[parseInt(p.substring(0, 2)) - 1]}/${p.substring(2)}`;
};

const formatBRL = (v) =>
    v?.toLocaleString("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 0 }) ?? "—";

// ─── Modal editar servidor ────────────────────────────────────────────────────
function ModalEditarServidor({ servidor, onFechar, onSalvo, token }) {
    const [form, setForm] = useState({
        nome:     servidor.nome     ?? "",
        email:    servidor.email    ?? "",
        telefone: servidor.telefone ?? "",
    });
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro]         = useState("");

    const handleSalvar = async () => {
        if (!form.nome) return setErro("Nome é obrigatório.");
        setSalvando(true);
        setErro("");
        try {
            await axios.put(`${API_URL}/api/users/${servidor.matricula}`, form, {
                headers: { Authorization: `Bearer ${token}` },
            });
            onSalvo();
        } catch {
            setErro("Erro ao salvar. Tente novamente.");
        } finally {
            setSalvando(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 px-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 flex flex-col gap-4">
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-bold text-gray-800">Editar Servidor</h2>
                    <button onClick={onFechar} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
                </div>

                <div className="flex flex-col gap-3">
                    <div>
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Matrícula</label>
                        <input value={servidor.matricula} disabled
                            className="mt-1 w-full h-10 px-3 rounded-lg border border-gray-100 text-sm bg-gray-50 text-gray-400 cursor-not-allowed" />
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Nome</label>
                        <input type="text" value={form.nome}
                            onChange={(e) => setForm({ ...form, nome: e.target.value })}
                            className="mt-1 w-full h-10 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition" />
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">E-mail</label>
                        <input type="email" value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            className="mt-1 w-full h-10 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition" />
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Telefone</label>
                        <input type="text" value={form.telefone}
                            onChange={(e) => setForm({ ...form, telefone: e.target.value })}
                            className="mt-1 w-full h-10 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition" />
                    </div>
                    {erro && <p className="text-xs text-red-500">{erro}</p>}
                </div>

                <div className="flex gap-3 pt-2">
                    <button onClick={onFechar}
                        className="flex-1 h-10 border border-gray-200 text-gray-500 text-sm font-semibold rounded-lg hover:bg-gray-50 transition-colors">
                        Cancelar
                    </button>
                    <button onClick={handleSalvar} disabled={salvando}
                        className="flex-1 h-10 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 text-white text-sm font-semibold rounded-lg transition-colors">
                        {salvando ? "Salvando..." : "Salvar"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function Dashboard() {
    const { token, usuario } = useAuth();
    const { notify } = useNotification();
    const { confirm } = useConfirmation();

    // Dashboard
    const [dash, setDash] = useState(null);
    const [grafAberto, setGrafAberto] = useState(true);

    // Servidores
    const [servidores, setServidores]   = useState([]);
    const [search, setSearch]           = useState("");
    const [page, setPage]               = useState(1);
    const [totalPages, setTotalPages]   = useState(1);
    const [total, setTotal]             = useState(0);
    const [modalEditar, setModalEditar] = useState(null);
    const debounceRef                   = useRef(null);
    const LIMIT = 10;

    // ── Fetch dashboard ────────────────────────────────────────────────────────
    useEffect(() => {
        fetch(`${API_URL}/api/dashboard`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.ok ? r.json() : null)
            .then(setDash)
            .catch(() => {});
    }, [token]);

    // ── Fetch servidores ───────────────────────────────────────────────────────
    const buscarServidores = useCallback(async (p = 1, termo = search) => {
        try {
            const res = await axios.get(`${API_URL}/api/users`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { page: p, limit: LIMIT, search: termo },
            });
            setServidores(res.data.data);
            setTotal(res.data.total);
            setTotalPages(res.data.totalPages);
            setPage(p);
        } catch {
            notify.error("Erro ao carregar servidores.");
        }
    }, [token, search]);

    useEffect(() => { buscarServidores(1); }, []);

    const handleSearch = (e) => {
        const termo = e.target.value;
        setSearch(termo);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => buscarServidores(1, termo), 350);
    };

    // ── Editar servidor ────────────────────────────────────────────────────────
    const handleSalvoEditar = () => {
        setModalEditar(null);
        notify.success("Servidor atualizado com sucesso!");
        buscarServidores(page);
    };

    // ── Excluir servidor ───────────────────────────────────────────────────────
    const handleExcluir = async (matricula, nome) => {
        const ok = await confirm(`Excluir servidor?`, `${nome} será removido permanentemente.`, "Excluir");
        if (!ok) return;
        try {
            await axios.delete(`${API_URL}/api/users/${matricula}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            notify.success("Servidor excluído.");
            buscarServidores(page);
        } catch {
            notify.error("Erro ao excluir servidor.");
        }
    };

    return (
        <div className="w-full max-w-5xl mx-auto flex flex-col gap-6 py-6 px-4">

            {/* Header */}
            <div className="border-b-2 border-blue-600 pb-3">
                <p className="text-xs font-semibold text-blue-400 uppercase tracking-widest mb-1">SINDIÁGUA-PB</p>
                <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
            </div>

            {/* Gráfico: Mensalidade — sócios e valor por período */}
            {dash?.mensalidadePorPeriodo?.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
                    <button
                        onClick={() => setGrafAberto((v) => !v)}
                        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors rounded-xl"
                    >
                        <div className="text-left">
                            <h2 className="text-sm font-semibold text-gray-700">Mensalidade por Período</h2>
                            {grafAberto && <p className="text-xs text-gray-400 mt-0.5">Quantidade de sócios e valor total arrecadado</p>}
                        </div>
                        <svg
                            className={`w-4 h-4 text-gray-400 transition-transform ${grafAberto ? "rotate-180" : ""}`}
                            fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>

                    {grafAberto && (
                    <div className="px-5 pb-5">
                    <ResponsiveContainer width="100%" height={280}>
                        <ComposedChart
                            data={dash.mensalidadePorPeriodo.map((r) => ({
                                ...r,
                                periodoLabel: formatarPeriodo(r.periodo),
                            }))}
                            margin={{ top: 4, right: 24, left: 0, bottom: 0 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="periodoLabel" tick={{ fontSize: 11 }} />
                            <YAxis
                                yAxisId="qtd"
                                orientation="left"
                                allowDecimals={false}
                                tick={{ fontSize: 11 }}
                                tickFormatter={(v) => v.toLocaleString("pt-BR")}
                                label={{ value: "Sócios", angle: -90, position: "insideLeft", offset: 10, style: { fontSize: 10, fill: "#6b7280" } }}
                            />
                            <YAxis
                                yAxisId="val"
                                orientation="right"
                                tick={{ fontSize: 11 }}
                                tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`}
                                label={{ value: "Valor (R$)", angle: 90, position: "insideRight", offset: 10, style: { fontSize: 10, fill: "#6b7280" } }}
                            />
                            <Tooltip
                                content={({ active, payload, label }) => {
                                    if (!active || !payload?.length) return null;
                                    const qtd = payload.find((p) => p.dataKey === "quantidade");
                                    const val = payload.find((p) => p.dataKey === "total");
                                    return (
                                        <div className="bg-white border border-gray-100 rounded-xl shadow-lg px-4 py-3 text-sm">
                                            <p className="font-semibold text-gray-700 mb-1">{label}</p>
                                            {qtd && <p style={{ color: qtd.color }}>{qtd.name}: {qtd.value.toLocaleString("pt-BR")} sócios</p>}
                                            {val && <p style={{ color: val.color }}>{val.name}: {formatBRL(val.value)}</p>}
                                        </div>
                                    );
                                }}
                            />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Bar yAxisId="qtd" dataKey="quantidade" name="Sócios" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                            <Line yAxisId="val" type="monotone" dataKey="total" name="Valor arrecadado" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                        </ComposedChart>
                    </ResponsiveContainer>
                    </div>
                    )}
                </div>
            )}

            {/* Lista de servidores */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                {/* Cabeçalho + busca */}
                <div className="px-6 py-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex-1">
                        <h2 className="text-sm font-semibold text-gray-700">Servidores</h2>
                    </div>
                    <div className="relative w-full sm:w-72">
                        <input type="text" value={search} onChange={handleSearch}
                            placeholder="Buscar por nome, matrícula ou cargo..."
                            className="w-full h-9 pl-9 pr-4 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition" />
                        <span className="absolute left-3 top-2 text-gray-400 text-sm">🔍</span>
                        {search && (
                            <button onClick={() => { setSearch(""); buscarServidores(1, ""); }}
                                className="absolute right-3 top-2 text-gray-400 hover:text-gray-600 text-sm">✕</button>
                        )}
                    </div>
                    <span className="text-xs text-gray-400 whitespace-nowrap">{total} servidor{total !== 1 ? "es" : ""}</span>
                </div>

                {/* Tabela */}
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-100">
                                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Matrícula</th>
                                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Nome</th>
                                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden md:table-cell">Cargo</th>
                                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {servidores.length === 0 ? (
                                <tr><td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-400">Nenhum servidor encontrado.</td></tr>
                            ) : servidores.map((s, i) => (
                                <tr key={s.matricula} className={`border-b border-gray-50 hover:bg-blue-50 transition-colors ${i % 2 !== 0 ? "bg-gray-50" : ""}`}>
                                    <td className="px-6 py-3 font-mono text-gray-500 text-xs">{s.matricula}</td>
                                    <td className="px-6 py-3 font-medium text-gray-700">{s.nome}</td>
                                    <td className="px-6 py-3 text-gray-500 hidden md:table-cell">{s.cargo ?? "—"}</td>
                                    <td className="px-4 py-3 text-center">
                                        <div className="flex items-center justify-center gap-2">
                                            <button onClick={() => setModalEditar(s)}
                                                className="px-3 py-1 text-xs font-semibold text-blue-600 hover:bg-blue-50 border border-blue-200 rounded-lg transition-colors">
                                                ✏ Editar
                                            </button>
                                            {usuario?.perfil === "admin" && (
                                            <button onClick={() => handleExcluir(s.matricula, s.nome)}
                                                className="px-3 py-1 text-xs font-semibold text-red-500 hover:bg-red-50 border border-red-200 rounded-lg transition-colors">
                                                🗑
                                            </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Paginação */}
                <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-between">
                    <p className="text-xs text-gray-400">
                        {total === 0 ? "0 resultados" : `${(page - 1) * LIMIT + 1}–${Math.min(page * LIMIT, total)} de ${total}`}
                    </p>
                    <div className="flex items-center gap-1">
                        <button onClick={() => buscarServidores(1)} disabled={page === 1}
                            className="px-2 py-1 text-xs font-semibold border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition">«</button>
                        <button onClick={() => buscarServidores(page - 1)} disabled={page === 1}
                            className="px-3 py-1 text-xs font-semibold border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition">← Ant.</button>
                        <span className="px-3 py-1 text-xs text-gray-500">{page} / {totalPages || 1}</span>
                        <button onClick={() => buscarServidores(page + 1)} disabled={page >= totalPages}
                            className="px-3 py-1 text-xs font-semibold border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition">Próx. →</button>
                        <button onClick={() => buscarServidores(totalPages)} disabled={page >= totalPages}
                            className="px-2 py-1 text-xs font-semibold border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition">»</button>
                    </div>
                </div>
            </div>

            {/* Modal editar */}
            {modalEditar && (
                <ModalEditarServidor
                    servidor={modalEditar}
                    token={token}
                    onFechar={() => setModalEditar(null)}
                    onSalvo={handleSalvoEditar}
                />
            )}
        </div>
    );
}
